import { useRouter } from 'next/router'
import ErrorPage from 'next/error'
import { motion } from 'framer-motion'
import PageType from '@interfaces/page'
import Layout from '@comp/layout'
import Seo from '@comp/seo'
import ProseArticle from '@comp/content/prose-article'
import Container from '@comp/ui/container'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import { getPages, getPageBySlug } from '@lib/api'
import markdownToHtml from '@lib/markdownToHtml'

const EASE = [0.22, 1, 0.36, 1] as const

type Props = {
  post: PageType
}

export default function LegalPage({ post }: Props) {
  const router = useRouter()
  const title = `Monofor | ${post.title}`

  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />
  }

  return (
    <Layout>
      <Seo
        title={title}
        description={post.excerpt || `${post.title}: Monofor legal documentation.`}
        path={`/legal/${post.slug}`}
        ogImage={post.ogImage?.url}
      />
      <main className="bg-surface text-ink-primary">
        <section className="bg-surface-subtle pt-20 pb-12 lg:pt-28 lg:pb-16 border-b border-line-subtle">
          <Container size="md">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
            >
              <Eyebrow tone="muted">Legal</Eyebrow>
            </motion.div>
            <motion.div
              className="mt-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: EASE, delay: 0.05 }}
            >
              <Heading as="h1" size="display-xl">
                {post.title}
              </Heading>
            </motion.div>
          </Container>
        </section>

        <article className="bg-surface pt-12 pb-24 lg:pt-16 lg:pb-32">
          <Container size="md">
            <ProseArticle html={post.content} />
          </Container>
        </article>
      </main>
    </Layout>
  )
}

type Params = {
  params: { slug: string }
}

export async function getStaticProps({ params }: Params) {
  const post = getPageBySlug('_pages/legal', params.slug, [
    'title',
    'excerpt',
    'date',
    'slug',
    'content',
    'ogImage',
    'coverImage',
  ])

  if (!post) {
    return { props: {} }
  }

  const content = await markdownToHtml(post.content || '')

  return {
    props: {
      post: {
        ...post,
        content,
      },
    },
  }
}

export async function getStaticPaths({ locales }: { locales: string[] }) {
  const posts = getPages('_pages/legal', ['slug'])
  // Emit every locale: TR footers link to these pages, and locale-less paths
  // only build the default locale (so /tr/legal/* used to 404). The TR routes
  // serve the EN text and canonical to the EN path (dora/gdpr pattern).
  const paths = posts
    .filter((post) => post.slug)
    .flatMap((post) =>
      (locales ?? ['en']).map((locale) => ({ params: { slug: post.slug }, locale }))
    )
  return { paths, fallback: false }
}
